import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Check, Languages } from "lucide-react";
import { useAppData } from "@/context/AppData";
import { api, track } from "@/lib/api";
import { STEPS, faqs } from "@/lib/content";
import { AudioPlayer } from "@/components/site/Audio";
import { PriceTag, PriceCTA } from "@/components/site/PriceTag";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const Vinyl = () => (
  <svg viewBox="0 0 200 200" className="h-full w-full" aria-hidden>
    <circle cx="100" cy="100" r="98" fill="#24172b" />
    {[88, 78, 68, 58, 48].map((r) => <circle key={r} cx="100" cy="100" r={r} fill="none" stroke="#c59b4e" strokeOpacity="0.22" strokeWidth="0.8" />)}
    <circle cx="100" cy="100" r="30" fill="#b56576" />
    <circle cx="100" cy="100" r="30" fill="none" stroke="#c59b4e" strokeWidth="1.5" />
    <circle cx="100" cy="100" r="3.5" fill="#faf7f2" />
  </svg>
);

const Hero = ({ examples }) => {
  const ex = examples[0];
  return (
    <section className="grain relative overflow-hidden">
      <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 pb-20 pt-16 sm:px-8 lg:grid-cols-12 lg:pt-24">
        <div className="lg:col-span-7">
          <p className="eyebrow rise">Personalized song gifts</p>
          <h1 className="font-display rise mt-4 text-4xl leading-[1.08] sm:text-5xl lg:text-6xl" data-testid="hero-headline">
            Your memories.<br /><em className="text-[var(--rose)]">Their favorite song.</em>
          </h1>
          <p className="rise mt-6 max-w-xl text-base text-[var(--plum-2)] md:text-lg" style={{ animationDelay: "0.1s" }}>
            Turn your story into a personalized song for someone you love.
          </p>
          <div className="rise mt-9 flex flex-wrap gap-4" style={{ animationDelay: "0.2s" }}>
            <Link to="/create" className="btn-gold" data-testid="hero-cta-button" onClick={() => track("creation_started")}>
              Create Their Song <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
        <div className="relative lg:col-span-5">
          <div className="mx-auto aspect-square w-64 sm:w-80"><div className="h-full w-full vinyl-spin"><Vinyl /></div></div>
          <div className="relative -mt-16 sm:mx-6">
            {ex ? (
              <AudioPlayer src={ex.audio_url} title={ex.title} subtitle={ex.description} testid="hero-audio-player" />
            ) : (
              <div className="card-white p-6 text-center" data-testid="hero-audio-empty">
                <p className="font-display text-lg">Example songs are on their way</p>
                <p className="mt-1 text-sm text-[var(--plum-muted)]">We only publish songs we have permission to share — no stand-ins.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

const Section = ({ eyebrow, title, children, className = "", testid }) => (
  <section className={`mx-auto max-w-7xl px-5 py-20 sm:px-8 ${className}`} data-testid={testid}>
    <p className="eyebrow">{eyebrow}</p>
    <h2 className="font-display mt-3 max-w-2xl text-3xl leading-tight sm:text-4xl">{title}</h2>
    <div className="mt-12">{children}</div>
  </section>
);

const Multilingual = ({ catalog, config }) => {
  const enabled = catalog.languages.filter((l) => l.available).sort((a, b) => b.popularity - a.popularity);
  const headline = config.multilingual_claim
    ? "Create a personalized song in over 40 languages, including English, Spanish, French, German, Hindi, Punjabi, Japanese, Korean, Arabic, Portuguese, Italian, and many more."
    : "Create a personalized song in your preferred language";
  return (
    <section className="bg-[var(--plum)] text-[var(--ivory)]" data-testid="multilingual-section">
      <div className="mx-auto grid max-w-7xl gap-12 px-5 py-20 sm:px-8 lg:grid-cols-2">
        <div>
          <p className="eyebrow !text-[var(--gold)]"><Languages className="mr-2 inline h-4 w-4" aria-hidden />Song language</p>
          <h2 className="font-display mt-3 text-3xl leading-tight sm:text-4xl" data-testid="multilingual-headline">{headline}</h2>
          <p className="mt-5 max-w-lg text-[var(--ivory)]/75">You can share your story in one language and choose another language for your song. Names stay exactly as you write them.</p>
          <p className="mt-3 max-w-lg text-xs text-[var(--ivory)]/50">Quality and pronunciation can vary between languages. We only offer languages our team has tested.</p>
        </div>
        <div className="flex flex-wrap content-start gap-3" data-testid="multilingual-enabled-list">
          {enabled.slice(0, 12).map((l) => (
            <span key={l.id} className="rounded-full border border-[var(--gold)]/40 px-5 py-2.5" data-testid={`multilingual-lang-${l.id}`}>
              <span className="font-display text-lg bidi-isolate" lang={l.code} dir={l.rtl ? "rtl" : "ltr"}>{l.native_name}</span>
              {l.native_name !== l.english_name && <span className="ml-2 text-sm text-[var(--ivory)]/60">{l.english_name}</span>}
            </span>
          ))}
          <p className="w-full text-sm text-[var(--ivory)]/60" data-testid="multilingual-count">
            {enabled.length} {enabled.length === 1 ? "language" : "languages"} available today{!config.multilingual_claim && " — more are being tested"}.
          </p>
        </div>
      </div>
    </section>
  );
};

export default function Home() {
  const { config, catalog } = useAppData();
  const navigate = useNavigate();
  const [examples, setExamples] = useState([]);
  useEffect(() => {
    track("landing_viewed");
    api.get("/examples").then((r) => setExamples(r.data)).catch(() => {});
  }, []);
  if (!config || !catalog) return <div className="min-h-[60vh]" />;
  const featured = config.featured_occasions.map((id) => catalog.occasions.find((o) => o.id === id)).filter(Boolean);

  return (
    <main data-testid="home-page">
      <Hero examples={examples} />
      <Section eyebrow="Occasions" title="A song for the moments that matter" testid="featured-occasions">
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {featured.map((o, i) => (
            <button key={o.id} onClick={() => { track("occasion_selected", { occasion_id: o.id }); navigate(`/create?occasion=${o.id}`); }}
              data-testid={`featured-occasion-${o.id}`} style={{ animationDelay: `${i * 0.05}s` }}
              className="card-parch rise group flex min-h-[120px] flex-col justify-between p-6 text-left hover:-translate-y-1 hover:border-[var(--gold)]">
              <span className="font-display text-xl sm:text-2xl">{o.label}</span>
              <span className="text-sm text-[var(--rose)] opacity-70 transition-opacity group-hover:opacity-100">Start this song →</span>
            </button>
          ))}
        </div>
        <Link to="/occasions" className="btn-ghost mt-8" data-testid="view-all-occasions">View All Occasions</Link>
      </Section>
      <Section eyebrow="How it works" title="From your memories to their song" className="!pt-4" testid="how-it-works-section">
        <ol className="grid gap-6 md:grid-cols-4">
          {STEPS.map((s, i) => (
            <li key={s.title} className="relative">
              <span className="font-display text-5xl text-[var(--gold)]/40">0{i + 1}</span>
              <s.icon className="mt-3 h-6 w-6 text-[var(--rose)]" aria-hidden />
              <h3 className="mt-3 text-lg font-semibold">{s.title}</h3>
              <p className="mt-2 text-sm text-[var(--plum-2)]">{s.text}</p>
            </li>
          ))}
        </ol>
      </Section>
      <Multilingual catalog={catalog} config={config} />
      <Section eyebrow="What's included" title={config.product_name} testid="pricing-section">
        <div className="grid gap-10 lg:grid-cols-2">
          <ul className="space-y-3" data-testid="inclusions-list">
            {config.inclusions.map((i) => <li key={i} className="flex gap-3"><Check className="mt-0.5 h-5 w-5 shrink-0 text-[var(--gold-deep)]" aria-hidden />{i}</li>)}
          </ul>
          <div className="card-parch p-8">
            <p className="text-sm text-[var(--plum-2)]">One-time purchase. No subscription.</p>
            <div className="mt-3"><PriceTag config={config} testid="home-price" /></div>
            {config.fulfillment_window && <p className="mt-3 text-sm text-[var(--plum-2)]">Typical delivery: {config.fulfillment_window}</p>}
            <p className="mt-4 text-xs text-[var(--plum-muted)]">{config.ai_disclosure}</p>
            <PriceCTA config={config} className="mt-6" testid="pricing-cta" />
          </div>
        </div>
      </Section>
      <Section eyebrow="Questions" title="Good to know" className="!pt-0" testid="faq-section">
        <Accordion type="single" collapsible className="max-w-3xl">
          {faqs(config).slice(0, 5).map(([q, a], i) => (
            <AccordionItem key={q} value={`f${i}`}>
              <AccordionTrigger className="text-left text-base" data-testid={`faq-q-${i}`}>{q}</AccordionTrigger>
              <AccordionContent className="text-[var(--plum-2)]">{a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Section>
      <section className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="card-parch grain relative overflow-hidden px-8 py-16 text-center sm:px-16">
          <h2 className="font-display text-3xl sm:text-4xl">Some memories deserve a melody.</h2>
          <Link to="/create" className="btn-gold mt-8" data-testid="final-cta">Create Their Song</Link>
        </div>
      </section>
    </main>
  );
}
